import { AssetPrewarmingService } from './AssetPrewarmingService';

/**
 * Lightweight Service Container — a typed singleton registry for
 * infrastructure-only services (caches, network bridges, queues) that:
 *
 *  - have lifetime state and should not be re-instantiated per consumer, and
 *  - are NOT MobX-observable, so they don't belong in the store graph.
 *
 * Unlike `src/di/container.ts` (the legacy token-based registry retained for
 * stores), this container is a plain class with typed fields. Adding a
 * service is a one-line edit; no decorators, tokens, or reflection.
 *
 * Stores receive specific services via constructor injection — they should
 * NOT reach into this container directly. React consumers access services
 * via `useAssetPrewarmingService()` (and future `useXService()` hooks)
 * exported from `ServiceContainerContext`.
 */
export interface ServiceContainerOverrides {
  /** Override the default `AssetPrewarmingService` (used by tests / Storybook). */
  assetPrewarming?: AssetPrewarmingService;
}

export class ServiceContainer {
  readonly assetPrewarming: AssetPrewarmingService;

  constructor(overrides: ServiceContainerOverrides = {}) {
    this.assetPrewarming =
      overrides.assetPrewarming ?? new AssetPrewarmingService();
  }
}

/**
 * Factory for a `ServiceContainer`. Prefer this over `new ServiceContainer()`
 * at call sites so the construction point reads as a registration step.
 */
export function createServiceContainer(
  overrides?: ServiceContainerOverrides,
): ServiceContainer {
  return new ServiceContainer(overrides);
}
